import { useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@inertiajs/react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ChevronDownIcon, ChevronUpIcon, AlertCircleIcon, RefreshCwIcon } from 'lucide-react'
import { MetricChart } from './MetricChart'
import { csrfHeaders } from '@/lib/csrf'
import { formatRelativeTime, uptimeColorClass } from './utils'
import type { DeployedProject, ProjectMetrics, Datapoint } from './types'

function formatTier(deployTier: string | null, computeTier: string): string {
  const compute = computeTier.charAt(0).toUpperCase() + computeTier.slice(1)
  if (!deployTier) return compute

  const deploy = deployTier
    .replace('frontend_only', 'Frontend Only')
    .replace('full_stack_no_workers', 'Full Stack')
    .replace('full_stack_with_workers', 'Full Stack + Workers')

  return `${compute} \u00B7 ${deploy}`
}

function MetricValue({ label, value, unit, warn }: { label: string; value: number | null | undefined; unit?: string; warn?: boolean }) {
  let formatted = '--'
  if (value != null) {
    switch (unit) {
      case 'Percent': formatted = `${value.toFixed(1)}%`; break
      case 'Count': formatted = value >= 1000 ? `${(value / 1000).toFixed(1)}K/h` : `${value.toFixed(0)}/h`; break
      case 'Milliseconds': formatted = `${value.toFixed(0)}ms`; break
      default: formatted = `${value.toFixed(0)} MB`
    }
  }

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-baseline justify-between gap-1 mb-1">
        <span className="text-xs text-muted-foreground truncate">{label}</span>
        <span className={`text-sm font-semibold tabular-nums ${warn ? 'text-red-600 dark:text-red-400' : ''}`}>
          {formatted}
        </span>
      </div>
    </div>
  )
}

const ERROR_THRESHOLD = 1 // 5xx rate above 1% = "down"

function UptimeBar({ datapoints }: { datapoints: Datapoint[] | null | undefined }) {
  const { t } = useTranslation('projects')

  if (!datapoints || datapoints.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">{t('production.apps.uptime')}:</span>
        <span className="text-xs text-muted-foreground">--</span>
      </div>
    )
  }

  const healthyCount = datapoints.filter(d => d.value < ERROR_THRESHOLD).length
  const uptimePercent = (healthyCount / datapoints.length) * 100

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground">{t('production.apps.uptime')}:</span>
      <div className="flex gap-0.5 flex-1">
        {datapoints.map((dp, i) => {
          const healthy = dp.value < ERROR_THRESHOLD
          const time = new Date(dp.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          return (
            <div
              key={i}
              className={`h-2.5 flex-1 rounded-sm cursor-default ${healthy ? 'bg-green-500' : 'bg-red-500'}`}
              title={`${time} — ${dp.value.toFixed(1)}% errors`}
            />
          )
        })}
      </div>
      <span className="text-xs font-medium tabular-nums">{uptimePercent.toFixed(1)}%</span>
    </div>
  )
}

export function AppMetricCard({ project, uptimePct }: { project: DeployedProject; uptimePct?: number }) {
  const { t } = useTranslation('projects')
  const [expanded, setExpanded] = useState(false)
  const [metrics, setMetrics] = useState<ProjectMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const fetchedRef = useRef(false)

  const fetchMetrics = useCallback(async () => {
    if (fetchedRef.current) return
    fetchedRef.current = true
    setLoading(true)
    setError(false)
    try {
      const res = await fetch(`/api/projects/${project.id}/production/metrics`, { headers: csrfHeaders() })
      if (!res.ok) throw new Error('Failed')
      const data = await res.json()
      setMetrics(data)
    } catch {
      fetchedRef.current = false
      setError(true)
    } finally {
      setLoading(false)
    }
  }, [project.id])

  const handleToggle = () => {
    const next = !expanded
    setExpanded(next)
    if (next && !metrics && !loading) {
      fetchMetrics()
    }
  }

  const hasIssues = project.unresolved_exceptions > 0
  const statusColor = hasIssues ? 'bg-red-500' : 'bg-green-500'
  const statusLabel = hasIssues ? t('production.platform.degraded') : t('production.platform.healthy')
  const statusBadgeClass = hasIssues
    ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800'
    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800'

  return (
    <Card className="shadow-none cursor-pointer hover:bg-muted/30 transition-colors" onClick={handleToggle}>
      <CardHeader className="pb-0">
        <div className="flex items-center gap-3 w-full">
<span className={`h-2.5 w-2.5 rounded-full shrink-0 ${statusColor}`} />

<Link
            href={project.url}
            className="font-semibold text-base hover:underline"
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            {project.name}
          </Link>
          <Badge variant="secondary" className={statusBadgeClass}>{statusLabel}</Badge>

          {uptimePct != null && (
            <span className={`text-xs font-medium tabular-nums hidden sm:inline ${uptimeColorClass(uptimePct)}`}>
              {uptimePct.toFixed(1)}% {t('production.apps.uptime').toLowerCase()}
            </span>
          )}

<div className="flex-1" />

<span className="text-xs text-muted-foreground hidden lg:inline">
            {formatTier(project.deploy_tier, project.compute_tier)}
          </span>

{project.unresolved_exceptions > 0 && (
            <Badge variant="secondary" className="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 gap-1">
              <AlertCircleIcon className="h-3 w-3" />
              {project.unresolved_exceptions}
            </Badge>
          )}

{project.production_deployed_at && (
            <span className="text-xs text-muted-foreground hidden md:inline">
              {t('production.apps.deployedAgo', { time: formatRelativeTime(project.production_deployed_at) })}
            </span>
          )}

{expanded
            ? <ChevronUpIcon className="h-4 w-4 text-muted-foreground shrink-0" />
            : <ChevronDownIcon className="h-4 w-4 text-muted-foreground shrink-0" />
          }
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="pt-4" onClick={(e) => e.stopPropagation()}>
          {loading && !metrics && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i}>
                    <Skeleton className="h-4 w-20 mb-2" />
                    <Skeleton className="h-[60px] w-full" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-3 w-full" />
            </div>
          )}

          {error && !metrics && (
            <div className="text-center py-4">
              <p className="text-sm text-muted-foreground mb-2">{t('production.apps.loadFailed')}</p>
              <Button variant="outline" size="sm" onClick={() => { fetchedRef.current = false; fetchMetrics() }}>
                <RefreshCwIcon className="h-3 w-3 mr-1" />
                {t('production.platform.retry')}
              </Button>
            </div>
          )}

          {metrics && (
            <div className="space-y-4">
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <MetricValue
                    label={t('production.apps.cpu')}
                    value={metrics.cpu?.current}
                    unit="Percent"
                    warn={(metrics.cpu?.current ?? 0) > 70}
                  />
                  <MetricChart datapoints={metrics.cpu?.datapoints} color="hsl(220, 70%, 55%)" height={60} label={t('production.apps.cpu')} unit="%" />
                </div>
                <div>
                  <MetricValue
                    label={t('production.apps.memory')}
                    value={metrics.memory?.current}
                    unit="Percent"
                  />
                  <MetricChart datapoints={metrics.memory?.datapoints} color="hsl(260, 60%, 55%)" height={60} label={t('production.apps.memory')} unit="%" />
                </div>
                <div>
                  <MetricValue
                    label={t('production.apps.requests')}
                    value={metrics.requests?.current}
                    unit="Count"
                  />
                  <MetricChart datapoints={metrics.requests?.datapoints} color="hsl(150, 60%, 45%)" height={60} label={t('production.apps.requests')} />
                </div>
                <div>
                  <MetricValue
                    label={t('production.apps.errorRate')}
                    value={metrics.error_rate_5xx?.current}
                    unit="Percent"
                    warn={(metrics.error_rate_5xx?.current ?? 0) > 1}
                  />
                  <MetricChart datapoints={metrics.error_rate_5xx?.datapoints} color="hsl(0, 70%, 55%)" height={60} label={t('production.apps.errorRate')} unit="%" />
                </div>
              </div>

<UptimeBar datapoints={metrics.error_rate_5xx?.datapoints} />

<div className="flex items-center justify-end gap-4 text-xs text-muted-foreground">
                {project.unresolved_exceptions > 0 ? (
                  <span className="text-orange-600 dark:text-orange-400 flex items-center gap-1">
                    <AlertCircleIcon className="h-3 w-3" />
                    {t('production.apps.exceptions', { count: project.unresolved_exceptions })}
                  </span>
                ) : (
                  <span>{t('production.apps.noExceptions')}</span>
                )}
                {project.production_deployed_at && (
                  <span>{t('production.apps.deployedAgo', { time: formatRelativeTime(project.production_deployed_at) })}</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
